import { styled } from 'styled-components'

interface AddClassModalProps {
    isOpen: boolean
    onClose: () => void
    onSubmit: () => void
    className: string
    onClassNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    isLoading?: boolean
    error?: string | null
}

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`

const ModalContent = styled.div`
    background-color: #ffffff;
    border-radius: 12px;
    padding: 32px;
    width: 90%;
    max-width: 400px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
`

const ModalTitle = styled.h3`
    font-size: 20px;
    font-weight: 600;
    color: #000000;
    margin: 0 0 20px 0;
`

const ModalInput = styled.input`
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #e5e6eb;
    border-radius: 6px;
    font-size: 14px;
    font-family: inherit;
    margin-bottom: 24px;
    box-sizing: border-box;
    transition: border-color 0.2s;

    &:focus {
        outline: none;
        border-color: #1fa474;
    }

    &::placeholder {
        color: #999999;
    }
`

const ModalButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
`

const CancelButton = styled.button`
    padding: 10px 24px;
    background-color: #f2f3f5;
    color: #333333;
    border: 1px solid #e5e6eb;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background-color: #e5e6eb;
    }
`

const SubmitButton = styled.button`
    padding: 10px 24px;
    background-color: #1fa474;
    color: #ffffff;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: #188c5f;
    }

    &:disabled {
        background-color: #cccccc;
        cursor: not-allowed;
    }
`

const ErrorMessage = styled.div`
    color: #d32f2f;
    font-size: 13px;
    margin-bottom: 16px;
    padding: 8px 12px;
    background-color: #ffebee;
    border-radius: 4px;
    border-left: 3px solid #d32f2f;
`

export default function AddClassModal({
    isOpen,
    onClose,
    onSubmit,
    className,
    onClassNameChange,
    isLoading = false,
    error = null,
}: AddClassModalProps) {
    if (!isOpen) return null

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalTitle>Create New Class</ModalTitle>
                {error && <ErrorMessage>{error}</ErrorMessage>}
                <ModalInput
                    type="text"
                    placeholder="Enter class name"
                    value={className}
                    onChange={onClassNameChange}
                    autoFocus
                    disabled={isLoading}
                />
                <ModalButtonGroup>
                    <CancelButton onClick={onClose} disabled={isLoading}>
                        Cancel
                    </CancelButton>
                    <SubmitButton
                        onClick={onSubmit}
                        disabled={!className.trim() || isLoading}
                    >
                        {isLoading ? 'Adding...' : 'Add'}
                    </SubmitButton>
                </ModalButtonGroup>
            </ModalContent>
        </ModalOverlay>
    )
}
